import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRightIcon, BookOpenIcon, CheckCircleIcon, ClipboardDocumentListIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
// start_date is a DATE column — never a JS Date. See lib/dateOnly.js.
import { formatDateOnlyNumeric } from '../../lib/dateOnly';
import { welcomeSteps } from './welcomeSteps';

// Landing page for new hires in pending_clearance / pending_hire /
// onboarding. Routes them to whichever step is next.

export default function MyPortalPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/my/profile')
      .then(({ data }) => setProfile(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Active employees go to the normal dashboard (no /my redirect loop).
  useEffect(() => {
    if (profile && profile.status === 'active') {
      navigate('/dashboard', { replace: true });
    }
  }, [profile, navigate]);

  if (loading) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-gray-400">Loading…</div></div>;
  }
  if (!profile) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-gray-400">Could not load your profile.</div></div>;
  }

  const steps = welcomeSteps(profile);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Welcome, {profile.first_name}!</h1>
            <p className="text-gray-500 text-sm mt-1">
              Status: <StatusBadge status={profile.status} />
            </p>
          </div>
          <button onClick={logout} className="text-sm text-gray-500 hover:text-gray-700">Sign out</button>
        </div>

        <div className="space-y-4">
          {steps.offer && (
            <StepCard
              icon={DocumentTextIcon}
              title="Step 1 — Review and sign your offer letter"
              body="Read and sign your conditional offer letter. Your signature is required before you can begin your clearance steps."
              action={
                <Link to="/my/offer-letter"
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 rounded-xl">
                  Open offer letter <ArrowRightIcon className="w-4 h-4" />
                </Link>
              }
            />
          )}

          {steps.policies.length > 0 && (
            <StepCard
              icon={BookOpenIcon}
              title="Read and acknowledge your policies"
              body={`Still to acknowledge: ${steps.policies.map((p) => p.title).join(' · ')}. `
                + 'Open each one, read it, and sign with your full name. This does not hold up your other steps.'}
              action={
                <Link to="/my/policies"
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 rounded-xl">
                  Open Policies <ArrowRightIcon className="w-4 h-4" />
                </Link>
              }
            />
          )}

          {steps.clearance && (
            <StepCard
              icon={ClipboardDocumentListIcon}
              title="Step 2 — Complete your clearance items"
              body="Drug test, fingerprinting, background check consent, ID, and proof of high school. Your admin handles some items; you handle others."
              action={
                <Link to="/my/onboarding"
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 rounded-xl">
                  Open my checklist <ArrowRightIcon className="w-4 h-4" />
                </Link>
              }
            />
          )}

          {steps.onboarding && (
            <StepCard
              icon={ClipboardDocumentListIcon}
              title="Step 3 — Onboarding"
              body={`You're cleared! Finish your training, payroll forms, and signed offer letter to wrap up. Start date: ${profile.start_date ? formatDateOnlyNumeric(profile.start_date) : 'TBD'}.`}
              action={
                <Link to="/my/onboarding"
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 rounded-xl">
                  Continue onboarding <ArrowRightIcon className="w-4 h-4" />
                </Link>
              }
            />
          )}

          {steps.clearanceWaitNote && (
            <p className="text-sm text-gray-500 text-center mt-4">
              Already finished signing? Your admin will start sending you to drug test, fingerprinting, etc. once clearance kicks off.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending_clearance: { label: 'Pre-start Clearance', cls: 'bg-blue-100 text-blue-700' },
    pending_hire:      { label: 'Pending hire',       cls: 'bg-blue-100 text-blue-700' },
    onboarding:        { label: 'Onboarding',         cls: 'bg-green-100 text-green-700' },
    active:            { label: 'Active',             cls: 'bg-green-100 text-green-700' },
  };
  const m = map[status] || { label: status, cls: 'bg-gray-100 text-gray-700' };
  return <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${m.cls}`}>{m.label}</span>;
}

function StepCard({ icon: Icon, title, body, action }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
          <Icon className="w-6 h-6 text-orange-500" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{title}</h3>
          <p className="text-sm text-gray-600 mb-4">{body}</p>
          {action}
        </div>
      </div>
    </div>
  );
}
